import SaveIcon from '@mui/icons-material/Save';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';

import { useGetPieceQuery, useUpdatePiecesMutation } from '@api/piece';
import { PieceAction } from '@apiContract';

import { ActionComponentProps } from '../../index';
import { loadingDialogSX } from './styles';

export const FinishMail = ({ mailpieces, actionStarted, onActionFinish }: ActionComponentProps) => {
  const [updatePieces] = useUpdatePiecesMutation();

  const [trackingNumber, setTrackingNumber] = useState('');
  const [actionInProgress, setActionInProgress] = useState<boolean>(false);

  const handleChangeTrackingNumber = (e: React.ChangeEvent<HTMLInputElement>) => setTrackingNumber(e.target.value);
  const { data, isLoading } = useGetPieceQuery(mailpieces[0].id);

  const confirmFinishMail = async () => {
    try {
      setActionInProgress(true);

      await updatePieces({
        pieces: mailpieces.map((piece) => ({ id: piece.id })),
        action: PieceAction.FinishMail,
        trackingNumber,
      }).unwrap();

      setActionInProgress(false);
      onActionFinish();
    } catch (error) {
      setActionInProgress(false);
    }
  };

  if (isLoading) {
    return (
      <Dialog maxWidth="sm" open={true} onClose={onActionFinish}>
        <Box sx={loadingDialogSX}>
          <CircularProgress size={40} />
        </Box>
      </Dialog>
    );
  }

  if (!data?.mailTo) {
    onActionFinish();
    return null;
  }

  const { addressLine1, addressLine2, administrativeArea, locality, postalCode, countryCode } = data.mailTo;

  return (
    <Dialog maxWidth="sm" open={actionStarted} onClose={actionInProgress ? () => {} : onActionFinish}>
      <DialogTitle sx={{ p: 3 }}>{mailpieces.length === 1 ? 'Mail item' : 'Mail selected items'}</DialogTitle>
      <DialogContent>
        <Stack direction="column" spacing={3}>
          <Typography variant="body1" color="text.secondary">
            {mailpieces.length === 1 ? (
              <>Please mail this item to the following address, and enter the tracking number if applicable.</>
            ) : (
              <>
                Please mail <strong>{mailpieces.length}</strong> item(s) to the following address, and enter the
                tracking number if applicable.
              </>
            )}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            <strong>
              {`${mailpieces[0].recipient?.firstName || ''} ${mailpieces[0].recipient?.lastName || ''}`}
              <br />
              {addressLine1}
              <br />
              {!!addressLine2 && (
                <>
                  {addressLine2}
                  <br />
                </>
              )}
              {`${locality} ${administrativeArea} ${postalCode}`}
              <br />
              {countryCode}
            </strong>
          </Typography>
          <TextField
            value={trackingNumber}
            onChange={handleChangeTrackingNumber}
            placeholder="Tracking Number"
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="text" color="inherit" onClick={onActionFinish} disabled={actionInProgress}>
          Cancel
        </Button>
        <Button
          onClick={confirmFinishMail}
          loading={actionInProgress}
          startIcon={actionInProgress && <SaveIcon />}
          loadingPosition={actionInProgress ? 'start' : undefined}
          variant="contained"
          color="primary"
        >
          Complete
        </Button>
      </DialogActions>
    </Dialog>
  );
};
